import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { ArrowsLeftRight, FolderSimplePlus, Warning } from "@phosphor-icons/react";

function TitleIssues() {
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState({});
  const load = useCallback(() => api.get("/policies/manage/title-issues").then((r) => {
    setRows(r.data); setPick(r.data.reduce((a, x) => ({ ...a, [x.id]: true }), {}));
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const apply = async () => {
    const ids = Object.keys(pick).filter((k) => pick[k]);
    if (!ids.length) return toast.error("Select rows to correct");
    const r = await api.post("/policies/manage/fix-titles", { ids });
    toast.success(`Corrected ${r.data.fixed}`); load();
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="title-issues">
      <div className="flex items-center gap-2 mb-1">
        <ArrowsLeftRight size={18} weight="bold" className="text-amber-600" />
        <h3 className="font-display text-lg font-bold text-slate-900">Title / reference correction</h3>
      </div>
      <p className="text-sm text-slate-500 mb-3">These legacy documents have a numeric value stored as the <b>title</b> and the real name stored as the <b>reference</b>. Review and apply the swap. IDs, revisions and signatures are unaffected.</p>
      {rows.length === 0 ? <p className="text-sm text-emerald-700">No title/reference issues found. 👍</p> : (
        <>
          <div className="flex flex-col gap-1 max-h-[340px] overflow-y-auto mb-3">
            {rows.map((r) => (
              <label key={r.id} data-testid={`title-issue-${r.id}`} className="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" data-testid={`title-check-${r.id}`} checked={!!pick[r.id]} onChange={(e) => setPick({ ...pick, [r.id]: e.target.checked })} />
                <span className="text-sm text-slate-400 font-mono w-24 truncate">{r.current_title}</span>
                <ArrowsLeftRight size={14} className="text-slate-300" />
                <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{r.proposed_title}</span>
                <span className="text-[11px] text-slate-400 font-mono">ref → {r.proposed_ref}</span>
              </label>
            ))}
          </div>
          <Button data-testid="fix-titles-btn" onClick={apply} className="bg-emerald-700 hover:bg-emerald-800">Apply corrections ({Object.values(pick).filter(Boolean).length})</Button>
        </>
      )}
    </div>
  );
}

function BulkCategorise() {
  const [docs, setDocs] = useState([]);
  const [sections, setSections] = useState([]);
  const [sid, setSid] = useState("");
  const [pick, setPick] = useState({});
  const [onlyUnfiled, setOnlyUnfiled] = useState(true);
  const load = useCallback(() => {
    api.get("/policies/manage/overview").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/policies/sections").then((r) => setSections(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const list = docs.filter((d) => !onlyUnfiled || (d.section_ids || []).length === 0);
  const apply = async (mode) => {
    if (!sid) return toast.error("Choose a section");
    const ids = Object.keys(pick).filter((k) => pick[k]);
    if (!ids.length) return toast.error("Select documents");
    const r = await api.post("/policies/manage/bulk-section", { section_id: sid, rams_ids: ids, mode });
    toast.success(`${mode === "remove" ? "Removed" : "Added"} ${r.data.changed}`); setPick({}); load();
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="bulk-categorise">
      <div className="flex items-center gap-2 mb-1">
        <FolderSimplePlus size={18} weight="bold" className="text-emerald-700" />
        <h3 className="font-display text-lg font-bold text-slate-900">Organise into sections</h3>
      </div>
      <p className="text-sm text-slate-500 mb-3">Assign existing documents to a library section in bulk. Uncategorised documents are shown by default.</p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select data-testid="bulk-section-select" value={sid} onChange={(e) => setSid(e.target.value)} className="h-9 rounded-md border border-slate-200 px-3 text-sm">
          <option value="">Choose section…</option>
          {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Button size="sm" data-testid="bulk-add-btn" onClick={() => apply("add")} className="bg-emerald-700 hover:bg-emerald-800">Add to section</Button>
        <Button size="sm" variant="outline" data-testid="bulk-remove-btn" onClick={() => apply("remove")}>Remove</Button>
        <label className="text-xs text-slate-500 inline-flex items-center gap-1 ml-2"><input type="checkbox" checked={onlyUnfiled} onChange={(e) => setOnlyUnfiled(e.target.checked)} /> Only uncategorised</label>
      </div>
      <div className="flex flex-col gap-1 max-h-[340px] overflow-y-auto" data-testid="categorise-list">
        {list.length === 0 && <p className="text-sm text-emerald-700 py-3">Everything is categorised. 👍</p>}
        {list.map((d) => (
          <label key={d.id} data-testid={`cat-${d.id}`} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" data-testid={`cat-check-${d.id}`} checked={!!pick[d.id]} onChange={(e) => setPick({ ...pick, [d.id]: e.target.checked })} />
            <span className="text-sm text-slate-800 flex-1 truncate">{d.title}</span>
            {d.content_state !== "structured" && <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><Warning size={10} weight="bold" />{d.content_state === "empty" ? "needs content" : d.content_state === "attachment" ? "attachment" : "no version"}</span>}
            <span className="text-[11px] text-slate-400 font-mono">{d.ref}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function LibrarySetup() {
  return (
    <div className="flex flex-col gap-5" data-testid="library-setup">
      <TitleIssues />
      <BulkCategorise />
    </div>
  );
}
